import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { FolderKanban, RefreshCcw, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

export const ProjectHeader = ({ project }) => {
  const [cutoff, setCutoff] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    api.get("/executive").then((r) => setCutoff(r.data?.date_range?.evidence_cutoff || ""));
  }, []);

  if (!project) return null;

  const saveCutoff = () => {
    api.patch("/admin/cutoff", { evidence_cutoff: draft }).then((r) => {
      setCutoff(r.data.evidence_cutoff);
      setEditing(false);
      toast.success(`Evidence cut-off set to ${r.data.evidence_cutoff} (audit-logged)`);
    }).catch((e) => toast.error(e.response?.data?.detail || "Update failed"));
  };

  const fields = [
    ["Asset", project.asset],
    ["Brand", project.brand],
    ["Scope", project.scope],
    ["Assessment", "Cross-sectional"],
  ];

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl px-4 py-3 shadow-sm shadow-slate-100" data-testid="project-header">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
            <FolderKanban className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-900 text-sm leading-tight truncate" data-testid="project-name">
              Elacestrant Global HCP Intelligence Assessment
            </p>
            <p className="text-[10.5px] text-slate-500 mt-0.5 truncate">Prepared by <span className="font-semibold text-slate-600">{project.prepared_by || "MarksMan Healthcare"}</span> · {project.company}</p>
          </div>
        </div>
        <div className="hidden xl:block h-8 w-px bg-slate-200" />
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 min-w-0 flex-1">
          {fields.map(([l, v]) => (
            <div key={l} className="shrink-0">
              <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-none">{l}</p>
              <p className="text-[11px] font-semibold text-slate-700 mt-0.5">{v}</p>
            </div>
          ))}
          <div className="min-w-0 max-w-xs hidden lg:block">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-none">Indication</p>
            <p className="text-[11px] font-medium text-slate-700 truncate mt-0.5" title={project.indication}>{project.indication}</p>
          </div>
          <div className="shrink-0">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 leading-none">Evidence cut-off</p>
            {editing ? (
              <span className="flex items-center gap-1 mt-0.5">
                <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)}
                  className="text-[11px] border border-slate-200 rounded px-1 py-0 h-5" data-testid="cutoff-input" />
                <button onClick={saveCutoff} data-testid="cutoff-save"><Check className="w-3.5 h-3.5 text-teal-600" /></button>
              </span>
            ) : (
              <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-1 mt-0.5" data-testid="cutoff-value">
                {cutoff || "—"}
                <button onClick={() => { setDraft(cutoff); setEditing(true); }} data-testid="cutoff-edit"><Pencil className="w-3 h-3 text-slate-400 hover:text-teal-600" /></button>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 ml-auto shrink-0">
          <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200" data-testid="review-status-badge">Scientific review: {project.review_status || "In Review"}</Badge>
          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200 gap-1 hidden 2xl:flex"><RefreshCcw className="w-3 h-3" />No refresh scheduled</Badge>
        </div>
      </div>
    </div>
  );
};
